/**
 * Hook global pour détecter les scans de code-barres
 *
 * Un scanner code-barres agit comme un clavier : il envoie les caractères
 * très rapidement puis (souvent) un Enter. Ce hook écoute au niveau du document
 * et détecte ce pattern PEU IMPORTE quel élément a le focus — donc PAS besoin
 * de cliquer dans la barre de recherche pour scanner.
 *
 * Robustesse (incident marché 08/06/2026 : le scan ne marchait qu'en cliquant la
 * barre de recherche) :
 * - Détection par CADENCE MOYENNE de la salve (et non reset au moindre intervalle
 *   lent), pour tolérer le jitter de certains scanners.
 * - Filet « scanner SANS Enter » : si le buffer est un code numérique saisi vite,
 *   il est traité après une courte pause, même sans touche Entrée.
 * - Les noms tapés à la main (lents) sont rejetés → pas de faux positifs.
 *
 * Quand un scan est détecté alors qu'un <input> a le focus, l'événement Enter est
 * stoppé (preventDefault + stopPropagation) et les caractères sont retirés du champ
 * pour éviter un double traitement. Un champ portant `data-scan-target` gère lui-même
 * le code (ex. champ EAN de réception) → pas de traitement global.
 */
import { useEffect, useRef } from 'react';
import { IS_TOUCH_DEVICE } from '../utils/touchDevice';

/**
 * Une douchette EST un clavier : ce hook ne peut pas distinguer un scan d'une
 * frappe rapide. Sur un appareil TACTILE, la saisie au clavier virtuel iOS
 * arrive par salves (correction automatique, saisie glissée, insertion
 * prédictive) qui passent le test de cadence — le hook la prend pour un scan,
 * RETIRE les caractères du champ et déclenche une recherche produit. Résultat
 * cote utilisateur : « clavier impossible à utiliser » (signalé le 09/08/2026).
 *
 * Un tactile SANS douchette scanne par la caméra (BarcodeScannerModal) : le hook
 * est donc inerte par défaut sur ces appareils.
 *
 * Quand une douchette est branchée à un appareil tactile — iPhone comme iPad,
 * la détection porte sur `maxTouchPoints`, pas sur la taille d'écran —, elle tape
 * comme un clavier et le hook doit reprendre son rôle : c'est le réglage
 * `pos_wedge_scanner`. Il est posé par un interrupteur visible à l'écran, et
 * relu à CHAQUE montage — un réglage qui n'aurait d'effet qu'après rechargement
 * donnerait un interrupteur qui ne fait apparemment rien.
 *
 * Protection du clavier virtuel : un champ portant `data-scan-target` n'est
 * jamais nettoyé ni interprété, la frappe y reste intacte même douchette active.
 */
export const WEDGE_SCANNER_STORAGE_KEY = 'pos_wedge_scanner';

export function isWedgeScannerEnabled(): boolean {
  try {
    return localStorage.getItem(WEDGE_SCANNER_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

interface UseBarcodeScanOptions {
  onScan: (barcode: string) => void;
  enabled?: boolean;
  /** Délai MOYEN max par caractère pour considérer un scanner (ms) */
  maxKeystrokeDelay?: number;
  /** Nombre minimum de caractères pour valider un scan (avec Enter) */
  minLength?: number;
  /**
   * Relit `pos_wedge_scanner` quand cette valeur change : permet à un
   * interrupteur d'écran d'activer la douchette sans recharger la page.
   */
  wedgeOnTouch?: boolean;
}

export function useBarcodeScan({
  onScan,
  enabled = true,
  maxKeystrokeDelay = 50,
  minLength = 5,
  wedgeOnTouch,
}: UseBarcodeScanOptions) {
  const bufferRef = useRef<string>('');
  const startRef = useRef<number>(0);
  const lastRef = useRef<number>(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onScanRef = useRef(onScan);

  // Garder la référence à jour sans recréer le listener
  onScanRef.current = onScan;

  useEffect(() => {
    if (!enabled) return;
    // Tactile : ne jamais intercepter la frappe, SAUF douchette declaree
    // (cf. commentaire en tête). Relu ici, donc sensible a l'interrupteur.
    if (IS_TOUCH_DEVICE && !(wedgeOnTouch ?? isWedgeScannerEnabled())) return;

    const IDLE_RESET_MS = 300;      // au-delà : nouvelle saisie → on repart à zéro
    const NO_ENTER_FLUSH_MS = 140;  // flush auto si le scanner n'envoie pas de Enter
    const NO_ENTER_MIN_DIGITS = 8;  // EAN-8 minimum pour un flush sans Enter

    // Numéro de commande ou de précommande. Sans ce filet, une douchette qui n'envoie
    // pas Entrée laisse les caractères dans le champ de recherche, où ils filtrent les
    // produits (« 0 résultats ») au lieu d'ouvrir la remise — les EAN, eux, passaient
    // par le test purement numérique juste à côté.
    //
    // Deux élargissements successifs, chacun né d'un symptôme en magasin :
    //  · le motif d'origine n'acceptait que 4+2..6 chiffres après le préfixe, taillé
    //    pour « PRE-2026-028 » ; un numéro de commande eshop vaut `CMD-${Date.now()}`
    //    (13 chiffres) ou 12 caractères HEXADÉCIMAUX (brouillons de checkout) — ni l'un
    //    ni l'autre ne passait, d'où « rien ne se passe » (12/09/2026) ;
    //  · le test porte sur la forme COMPACTÉE, car les séparateurs varient (« PRE-2026-028 »
    //    en a deux, le code-barres du ticket aucun) : les énumérer dans le motif casse
    //    à chaque variante — c'est ce qui avait fait sauter le cas tapé à la main.
    //
    // Le garde-fou reste la CADENCE (isFastScan) : une frappe humaine ne l'atteint pas.
    const CODE_COMMANDE = /^(PRE|CMD)[A-Z0-9]{6,20}$/;
    const estCodeCommande = (s: string) =>
      CODE_COMMANDE.test(s.toUpperCase().replace(/[^A-Za-z0-9]/g, ''));

    const clearFlush = () => {
      if (flushTimerRef.current) {
        clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
    };
    const reset = () => {
      bufferRef.current = '';
      startRef.current = 0;
      lastRef.current = 0;
      clearFlush();
    };

    // Cadence « scanner » : moyenne par caractère <= maxKeystrokeDelay sur TOUTE la salve.
    // Tolère un intervalle ponctuellement lent (jitter) que l'ancien reset par-frappe cassait.
    const isFastScan = (): boolean => {
      const len = bufferRef.current.length;
      if (len < 2) return false;
      const total = lastRef.current - startRef.current;
      return total <= (len - 1) * maxKeystrokeDelay;
    };

    // Retirer les caractères du scanner qui ont atterri dans un input focalisé
    const cleanInput = (target: Element | null, barcode: string) => {
      if (target instanceof HTMLInputElement && !target.hasAttribute('data-scan-target')) {
        const val = target.value;
        if (val.endsWith(barcode)) {
          const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
          setter?.call(target, val.slice(0, -barcode.length));
          target.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    };

    const emit = (barcode: string, target: Element | null) => {
      const isScanTarget = target instanceof HTMLElement && target.hasAttribute('data-scan-target');
      reset();
      cleanInput(target, barcode);
      // data-scan-target gère lui-même le code (champ EAN réception) → pas de traitement global
      if (!isScanTarget) onScanRef.current(barcode);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const now = Date.now();

      // Enter → valider le scan si la salve a une cadence de scanner
      if (event.key === 'Enter') {
        if (bufferRef.current.length >= minLength && isFastScan()) {
          event.preventDefault();
          event.stopPropagation();
          emit(bufferRef.current, event.target as Element);
        } else {
          // Saisie manuelle (lente) → laisser le handler du champ gérer le Enter
          reset();
        }
        return;
      }

      // Ignorer les touches spéciales (Shift, Ctrl, Alt, F1-F12, flèches…)
      if (event.key.length !== 1) return;

      // Nouvelle salve si trop de temps depuis la dernière frappe
      if (bufferRef.current.length > 0 && now - lastRef.current > IDLE_RESET_MS) {
        bufferRef.current = '';
        startRef.current = 0;
      }
      if (bufferRef.current.length === 0) startRef.current = now;
      bufferRef.current += event.key;
      lastRef.current = now;

      // Filet « scanner SANS Enter » : après une courte pause, si le buffer est un code
      // numérique saisi vite, on le traite. (Les noms tapés à la main sont trop lents.)
      clearFlush();
      flushTimerRef.current = setTimeout(() => {
        const buf = bufferRef.current;
        const estCode =
          (buf.length >= NO_ENTER_MIN_DIGITS && /^\d+$/.test(buf)) || estCodeCommande(buf);
        if (estCode && isFastScan()) {
          emit(buf, document.activeElement);
        }
      }, NO_ENTER_FLUSH_MS);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      reset();
    };
  }, [enabled, maxKeystrokeDelay, minLength, wedgeOnTouch]);
}
